/**
 * CreatePageContext - Page-specific context for create challenge page
 * Provides all create page data, actions, and state
 */

import React, { createContext, useContext, useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useFriendsData } from '../../../contexts/FriendsDataContext';
import { USER_CONFIG } from '../../../constants';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { getUserDisplayName } from '../../friends/helpers/friendsUser.helper';
import { useCreateGames } from '../hooks/useCreateGames';
import { usePickSlips } from '../hooks/usePickSlips';
import { useCreateFormState } from '../hooks/useCreateFormState';
import { useSlatePrefillConsumer } from '../hooks/useSlatePrefillConsumer';
import { useParticipantSelection } from '../hooks/useParticipantSelection';
import { useChallengeCreation } from '../hooks/useChallengeCreation';
import type {
  Game,
  PickSlip,
  CreateStep,
  BuildView,
  ParticipantTab,
  EditState,
} from '../types/create.types';
import type { Group } from '../../../types/challenges';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useTrackingPickCallbacks } from '../hooks/useTrackingPickCallbacks';
import { useGuestMonkeyGuard } from '../hooks/useGuestMonkeyGuard';
import { useCreateFlowStep } from '../hooks/useCreateFlowStep';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { useDialogStore } from '../../shared/stores/dialogStore';
import { useGuestWalkthroughStore } from '../../shared/stores/guestWalkthroughStore'
import { buildPublicSignInHref } from '../../signIn/helpers/postAuthRedirect.helper';
import { useAccountAdmin } from '../../accounts/hooks/useAccountAdmin';
import { MAX_GAMES_PER_CHALLENGE, MAX_GAMES_PER_CHALLENGE_PRO } from '../constants/create.constants';

export interface CreatePageContextValue {
  // User info
  currentUserId: string;
  friends: ReturnType<typeof useFriendsData>['friends'];
  friendsLoading: boolean;

  // Games data
  games: Game[];
  gamesLoading: boolean;
  filteredGames: Game[];
  gamesByLeague: Record<string, Game[]>;

  // Pick slips
  pickSlips: PickSlip[];
  totalPoints: number;
  selectedGamesByLeague: Record<string, Array<{ game: Game; slip: PickSlip }>>;
  handlePickSelect: (gameId: string, pick: 'home' | 'draw' | 'away' | null) => void;
  handleUpdatePick: (gameId: string, prediction: 'home' | 'draw' | 'away') => void;
  handleRemovePick: (gameId: string) => void;
  handleRemoveSlip: (slip: PickSlip) => void;
  handleDeleteMarkets: (gameId: string, marketIds: number[]) => void;
  getPickSlipForGame: (gameId: string) => PickSlip | undefined;
  setPickSlips: React.Dispatch<React.SetStateAction<PickSlip[]>>;
  handleAddOtherPick: (gameId: string, marketId: number, marketName: string, value: string, odd: number, homeTeam?: string, awayTeam?: string) => void;
  isOtherPickSelected: (gameId: string, marketId: number, value: string) => boolean;

  // Form state
  step: CreateStep;
  setStep: React.Dispatch<React.SetStateAction<CreateStep>>;
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  editStates: Record<string, EditState>;
  setEditStates: React.Dispatch<React.SetStateAction<Record<string, EditState>>>;
  /** Build screen sub-view: 'deck' (default) or 'list'. Not a step — toggling it does not change step history. */
  buildView: BuildView;
  setBuildView: React.Dispatch<React.SetStateAction<BuildView>>;
  /** Current deck card index for the "one at a time" build view. */
  deckIdx: number;
  setDeckIdx: React.Dispatch<React.SetStateAction<number>>;
  scrollViewRef: React.RefObject<ScrollView | null>;
  resetForm: () => void;

  // Participant selection
  activeTab: ParticipantTab;
  setActiveTab: React.Dispatch<React.SetStateAction<ParticipantTab>>;
  selectedFriendId: string | null;
  setSelectedFriendId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedGroupId: string | null;
  setSelectedGroupId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedGroup: Group | null;
  setSelectedGroup: React.Dispatch<React.SetStateAction<Group | null>>;
  availableGroups: Group[];
  groupsLoading: boolean;
  resetSelection: () => void;

  // Stake
  stakeCoins: number;
  setStakeCoins: React.Dispatch<React.SetStateAction<number>>;
  userCoins: number;

  // Challenge creation
  isSendingChallenge: boolean;
  handleSendChallenge: () => Promise<void>;

  /** Guest-only: mirage flow vs Monkey (no API until sign-up). */
  isGuestMonkeyCreateFlow: boolean;

  /** Max games allowed per challenge for the current user's plan tier. */
  maxGames: number;

  // Rival-first flow navigation (specs/challenge-flow-redesign)
  /** Advance rival → build (no-op without a selected opponent). */
  toBuild: () => void;
  /** Advance build → review (no-op without ≥2 picks + rival). */
  toReview: () => void;
  /** Back one step: build → rival, review → build. */
  back: () => void;
  /** True when the slate can be reviewed (rival + ≥2 picks). */
  canReview: boolean;
}

export const CreatePageContext = createContext<CreatePageContextValue | null>(null);

/** Form-only context so step-only consumers don't re-render when pickSlips change. */
export interface CreatePageFormContextValue {
  step: CreateStep;
  setStep: React.Dispatch<React.SetStateAction<CreateStep>>;
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  editStates: Record<string, EditState>;
  setEditStates: React.Dispatch<React.SetStateAction<Record<string, EditState>>>;
  scrollViewRef: React.RefObject<ScrollView | null>;
  resetForm: () => void;
}

export const CreatePageFormContext = createContext<CreatePageFormContextValue | null>(null);

interface CreatePageProviderProps {
  children: React.ReactNode;
  /** Optional initial step (e.g. for Storybook). */
  initialStep?: CreateStep;
  /** Optional initial pick slips (e.g. for Storybook). */
  initialPickSlips?: PickSlip[];
}

export const CreatePageProvider: React.FC<CreatePageProviderProps> = ({
  children,
  initialStep,
  initialPickSlips,
}) => {
  const { userId: authUserId, getToken, user: authUser, isAnonymous } = useSupabaseAuth();
  const currentUserId = authUserId || USER_CONFIG.defaultUserId;
  const router = useRouter();
  const { showSnackbar } = useSnackbar();
  // Anonymous users can only challenge the Monkey — mirror the old guest flow UX
  const isGuestMonkeyCreateFlow = isAnonymous;

  // Get friends from global context
  const { friends, loading: friendsLoading } = useFriendsData();
  const friendsForCreate = friends;

  // Form state (needed for games hook)
  const { selectedDate, stakeCoins, setStakeCoins, ...formState } = useCreateFormState(initialStep);

  const userCoins = useEconomyStore((s) => s.coins);

  // Fetch is scoped by CreatePageProvider (create stack only); do not gate on pathname — some paths/hydration
  // omit "create" and would leave guests stuck with no games + a calendar that appears broken.
  const { games, loading: gamesLoading, filteredGames, gamesByLeague } = useCreateGames(selectedDate, true);

  // Derive max games per challenge from the user's plan tier (stable — never regresses from PRO to FREE mid-session)
  const { user: accountUser, loading: accountLoading } = useAccountAdmin();
  const resolvedMaxGames = accountUser?.consumerBilling?.planTier === 'PRO' ? MAX_GAMES_PER_CHALLENGE_PRO : MAX_GAMES_PER_CHALLENGE;
  const maxGamesRef = useRef(resolvedMaxGames);
  if (resolvedMaxGames > maxGamesRef.current) maxGamesRef.current = resolvedMaxGames;
  const maxGames = accountLoading ? maxGamesRef.current : resolvedMaxGames;

  // Pick slips
  const pickSlipsData = usePickSlips(games, initialPickSlips, maxGames);

  // Participant selection
  const participantSelection = useParticipantSelection(currentUserId);

  // Home's "Send to a Friend" / "Beat the Monkey" hand a prepared slate over through a store
  // (specs/home-page AC-10, AC-10b); the Monkey variant also picks the rival and opens at review.
  useSlatePrefillConsumer({
    setPickSlips: pickSlipsData.setPickSlips,
    setStep: formState.setStep,
    setSelectedDate: formState.setSelectedDate,
    setSelectedFriendId: participantSelection.setSelectedFriendId,
    setSelectedGroupId: participantSelection.setSelectedGroupId,
    setSelectedGroup: participantSelection.setSelectedGroup,
  });

  useGuestMonkeyGuard({
    isGuestMonkeyCreateFlow,
    selectedFriendId: participantSelection.selectedFriendId,
    selectedGroupId: participantSelection.selectedGroupId,
    activeTab: participantSelection.activeTab,
    setSelectedFriendId: participantSelection.setSelectedFriendId,
    setSelectedGroupId: participantSelection.setSelectedGroupId,
    setSelectedGroup: participantSelection.setSelectedGroup,
    setActiveTab: participantSelection.setActiveTab,
  });

  const [isSendingChallenge, setIsSendingChallenge] = useState(false);

  // Rival-first flow bypasses the old games-first guided tour (specs/challenge-flow-redesign Q-5):
  // its spotlight steps are wired to the old order and would soft-lock here. End the tour gracefully
  // once the user enters create so no stale spotlight is left hanging. Re-sequencing is a follow-up.
  const dismissWalkthrough = useGuestWalkthroughStore((s) => s.dismissWalkthrough);
  const walkthroughActive = useGuestWalkthroughStore((s) => s.isActive);
  const bypassedTourRef = useRef(false);
  useEffect(() => {
    if (bypassedTourRef.current || !walkthroughActive) return;
    bypassedTourRef.current = true;
    dismissWalkthrough();
  }, [walkthroughActive, dismissWalkthrough]);

  // Challenge creation
  const { handleSendChallenge: handleSendChallengeHook } = useChallengeCreation({
    getToken,
    getUserDisplayName: () => getUserDisplayName(authUser),
  });

  const { track } = useAnalytics();

  // Rival-first step navigation + guards (rival → build → review).
  const hasRival = !!(participantSelection.selectedFriendId || participantSelection.selectedGroupId);
  const {
    toBuild,
    toReview,
    back,
    canReview,
  } = useCreateFlowStep({
    step: formState.step,
    setStep: formState.setStep,
    hasRival,
    pickCount: pickSlipsData.pickSlips.length,
  });

  const {
    handleRemovePickWithTracking,
    handleRemoveSlipWithTracking,
    handleDeleteMarketsWithTracking,
  } = useTrackingPickCallbacks({
    step: formState.step,
    pickSlipsLength: pickSlipsData.pickSlips.length,
    handleRemovePick: pickSlipsData.handleRemovePick,
    handleRemoveSlip: pickSlipsData.handleRemoveSlip,
    handleDeleteMarkets: pickSlipsData.handleDeleteMarkets,
  });

  useEffect(() => {
    track(ANALYTICS_EVENTS.CREATE_CHALLENGE_STEP_VIEWED, {
      step: formState.step,
      games_count: filteredGames.length,
      picks_count: pickSlipsData.pickSlips.length,
      participants_count:
        participantSelection.selectedFriendId || participantSelection.selectedGroupId ? 1 : 0,
    });
  }, [formState.step]); // eslint-disable-line react-hooks/exhaustive-deps -- only track on step change

  /**
   * No picks left while reviewing — fall back to the build screen so the user is never
   * stranded on an empty slate. Do NOT bounce out of 'build' (the user is mid-building),
   * and keep the selected rival intact.
   */
  useEffect(() => {
    if (pickSlipsData.pickSlips.length > 0) return;
    if (formState.step !== 'review') return;
    formState.setStep('build');
    formState.setEditStates({});
  }, [pickSlipsData.pickSlips.length, formState.step, formState.setStep, formState.setEditStates]);

  const handleSendChallenge = useCallback(async () => {
    if (isSendingChallenge) return;

    const anonymousSelectionInvalid =
      isGuestMonkeyCreateFlow
      && (participantSelection.selectedGroupId != null
        || participantSelection.selectedFriendId !== MONKEY_USER_ID);
    if (anonymousSelectionInvalid) {
      void useDialogStore.getState().showDialog({
        title: 'Sign up to continue',
        message: 'Create a free account to challenge friends or groups.',
        confirmText: 'Sign Up',
        cancelText: 'Not now',
      }).then((confirmed) => {
        if (confirmed) {
          router.replace(buildPublicSignInHref({ signup: true }) as `/${string}`);
        }
      });
      return;
    }

    setIsSendingChallenge(true);
    formState.setStep('success');
    await handleSendChallengeHook(
      pickSlipsData.pickSlips,
      games,
      participantSelection.selectedFriendId,
      participantSelection.selectedGroupId,
      participantSelection.selectedGroup,
      friends,
      currentUserId,
      () => {
        setIsSendingChallenge(false);
      },
      () => {
        // Send failed — return to the build screen with picks + rival intact (AC-5.3).
        formState.setStep('build');
        setIsSendingChallenge(false);
      },
      stakeCoins,
      maxGames
    );
  }, [
    isSendingChallenge,
    isGuestMonkeyCreateFlow,
    handleSendChallengeHook,
    pickSlipsData.pickSlips,
    games,
    participantSelection.selectedFriendId,
    participantSelection.selectedGroupId,
    participantSelection.selectedGroup,
    friends,
    currentUserId,
    formState.setStep,
    stakeCoins,
    showSnackbar,
    router,
  ]);

  const formValue: CreatePageFormContextValue = useMemo(
    () => ({
      step: formState.step,
      setStep: formState.setStep,
      selectedDate,
      setSelectedDate: formState.setSelectedDate,
      editStates: formState.editStates,
      setEditStates: formState.setEditStates,
      scrollViewRef: formState.scrollViewRef,
      resetForm: formState.resetForm,
    }),
    [
      formState.step,
      formState.setStep,
      selectedDate,
      formState.setSelectedDate,
      formState.editStates,
      formState.setEditStates,
      formState.scrollViewRef,
      formState.resetForm,
    ]
  );

  const value: CreatePageContextValue = useMemo(
    () => ({
      currentUserId,
      friends: friendsForCreate,
      friendsLoading: isGuestMonkeyCreateFlow ? false : friendsLoading,
      games,
      gamesLoading,
      filteredGames,
      gamesByLeague,
      pickSlips: pickSlipsData.pickSlips,
      totalPoints: pickSlipsData.totalPoints,
      selectedGamesByLeague: pickSlipsData.selectedGamesByLeague,
      handlePickSelect: pickSlipsData.handlePickSelect,
      handleUpdatePick: pickSlipsData.handleUpdatePick,
      handleRemovePick: handleRemovePickWithTracking,
      handleRemoveSlip: handleRemoveSlipWithTracking,
      handleDeleteMarkets: handleDeleteMarketsWithTracking,
      getPickSlipForGame: pickSlipsData.getPickSlipForGame,
      setPickSlips: pickSlipsData.setPickSlips,
      handleAddOtherPick: pickSlipsData.handleAddOtherPick,
      isOtherPickSelected: pickSlipsData.isOtherPickSelected,
      step: formState.step,
      setStep: formState.setStep,
      selectedDate,
      setSelectedDate: formState.setSelectedDate,
      editStates: formState.editStates,
      setEditStates: formState.setEditStates,
      buildView: formState.buildView,
      setBuildView: formState.setBuildView,
      deckIdx: formState.deckIdx,
      setDeckIdx: formState.setDeckIdx,
      scrollViewRef: formState.scrollViewRef,
      resetForm: formState.resetForm,
      activeTab: participantSelection.activeTab,
      setActiveTab: participantSelection.setActiveTab,
      selectedFriendId: participantSelection.selectedFriendId,
      setSelectedFriendId: participantSelection.setSelectedFriendId,
      selectedGroupId: participantSelection.selectedGroupId,
      setSelectedGroupId: participantSelection.setSelectedGroupId,
      selectedGroup: participantSelection.selectedGroup,
      setSelectedGroup: participantSelection.setSelectedGroup,
      availableGroups: participantSelection.availableGroups,
      groupsLoading: participantSelection.groupsLoading,
      resetSelection: participantSelection.resetSelection,
      stakeCoins,
      setStakeCoins,
      userCoins,
      isSendingChallenge,
      handleSendChallenge,
      isGuestMonkeyCreateFlow,
      maxGames,
      toBuild,
      toReview,
      back,
      canReview,
    }),
    [
      currentUserId,
      friendsForCreate,
      friendsLoading,
      isGuestMonkeyCreateFlow,
      games,
      gamesLoading,
      filteredGames,
      gamesByLeague,
      pickSlipsData.pickSlips,
      pickSlipsData.totalPoints,
      pickSlipsData.selectedGamesByLeague,
      pickSlipsData.handlePickSelect,
      pickSlipsData.handleUpdatePick,
      handleRemovePickWithTracking,
      handleRemoveSlipWithTracking,
      handleDeleteMarketsWithTracking,
      pickSlipsData.getPickSlipForGame,
      pickSlipsData.setPickSlips,
      pickSlipsData.handleAddOtherPick,
      pickSlipsData.isOtherPickSelected,
      formState.step,
      formState.setStep,
      selectedDate,
      formState.setSelectedDate,
      formState.editStates,
      formState.setEditStates,
      formState.buildView,
      formState.setBuildView,
      formState.deckIdx,
      formState.setDeckIdx,
      formState.scrollViewRef,
      formState.resetForm,
      participantSelection.activeTab,
      participantSelection.setActiveTab,
      participantSelection.selectedFriendId,
      participantSelection.setSelectedFriendId,
      participantSelection.selectedGroupId,
      participantSelection.setSelectedGroupId,
      participantSelection.selectedGroup,
      participantSelection.setSelectedGroup,
      participantSelection.availableGroups,
      participantSelection.groupsLoading,
      participantSelection.resetSelection,
      stakeCoins,
      setStakeCoins,
      userCoins,
      isSendingChallenge,
      handleSendChallenge,
      maxGames,
      toBuild,
      toReview,
      back,
      canReview,
    ]
  );

  return (
    <CreatePageFormContext.Provider value={formValue}>
      <CreatePageContext.Provider value={value}>
        {children}
      </CreatePageContext.Provider>
    </CreatePageFormContext.Provider>
  );
};

export const useCreatePage = (): CreatePageContextValue => {
  const context = useContext(CreatePageContext);
  if (!context) {
    throw new Error('useCreatePage must be used within CreatePageProvider');
  }
  return context;
};

/** Form state only. Use when component only needs step/setStep so it doesn't re-render on pickSlips change. */
export const useCreatePageForm = (): CreatePageFormContextValue => {
  const context = useContext(CreatePageFormContext);
  if (!context) {
    throw new Error('useCreatePageForm must be used within CreatePageProvider');
  }
  return context;
};

/** Returns context or null when outside provider. Use when context is optional (e.g. MatchDetailsScreen preferring context for team names, falling back to params). */
export const useCreatePageOptional = (): CreatePageContextValue | null => {
  return useContext(CreatePageContext);
};
